import React, { useState, useEffect } from 'react';
import { 
  X, Brain, FileText, CheckCircle, AlertCircle, Sparkles, 
  Eye, EyeOff, ChevronDown, ChevronUp, Bug, Clock, Database,
  Zap, ChevronRight, Save, Building2, User, Euro, Calendar,
  CreditCard, Home, Settings, Gavel, Megaphone, Hash, Percent,
  TrendingUp, Users, Globe, BarChart3, Shield, BookOpen
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { masterSupabase } from '../../lib/supabase-master.js';

const DocumentAnalysisModal = ({ document, isOpen, onClose }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTechnicalDetails, setShowTechnicalDetails] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [debugData, setDebugData] = useState(null);
  const [debugLoading, setDebugLoading] = useState(false);
  const [debugError, setDebugError] = useState(null);
  
  // Enhanced state for intelligent form
  const [formData, setFormData] = useState({});
  const [smartMatches, setSmartMatches] = useState({});
  const [externalInsights, setExternalInsights] = useState([]);
  const [memoryInsights, setMemoryInsights] = useState([]);
  const [saving, setSaving] = useState(false);

  // Document type configurations
  const documentTypeConfig = {
    invoice: {
      title: 'Rechnung verarbeiten',
      icon: FileText,
      color: 'blue',
      fields: [
        { key: 'supplier_name', label: 'Lieferant', type: 'select', table: 'suppliers' },
        { key: 'invoice_number', label: 'Rechnungsnummer', type: 'text' },
        { key: 'invoice_date', label: 'Rechnungsdatum', type: 'date' },
        { key: 'due_date', label: 'Fälligkeitsdatum', type: 'date' },
        { key: 'amount_net', label: 'Nettobetrag', type: 'currency' },
        { key: 'vat_rate', label: 'MwSt. (%)', type: 'number' },
        { key: 'amount_gross', label: 'Bruttobetrag', type: 'currency' },
        { key: 'category', label: 'Kategorie', type: 'select', options: ['operating', 'management', 'financing', 'legal', 'marketing'] }
      ],
      saveTable: 'operating_costs'
    },
    contract: {
      title: 'Vertrag verarbeiten',
      icon: FileText,
      color: 'green',
      fields: [
        { key: 'contract_party', label: 'Vertragspartner', type: 'select', table: 'tenants' },
        { key: 'contract_number', label: 'Vertragsnummer', type: 'text' },
        { key: 'contract_type', label: 'Vertragsart', type: 'text' },
        { key: 'start_date', label: 'Vertragsbeginn', type: 'date' },
        { key: 'end_date', label: 'Vertragsende', type: 'date' },
        { key: 'contract_value', label: 'Vertragswert', type: 'currency' },
        { key: 'property_reference', label: 'Objektbezug', type: 'select', table: 'properties' },
        { key: 'termination_period', label: 'Kündigungsfrist', type: 'text' }
      ],
      saveTable: 'contracts'
    },
    general: {
      title: 'Dokument verarbeiten',
      icon: FileText,
      color: 'purple',
      fields: [
        { key: 'document_name', label: 'Dokumentname', type: 'text' },
        { key: 'document_date', label: 'Dokumentdatum', type: 'date' },
        { key: 'category', label: 'Kategorie', type: 'text' },
        { key: 'description', label: 'Beschreibung', type: 'textarea' },
        { key: 'related_property', label: 'Bezogenes Objekt', type: 'select', table: 'properties' }
      ],
      saveTable: 'documents'
    }
  };

  useEffect(() => {
    if (isOpen && document) {
      console.log('🔍 Modal opened for document:', document.id);
      checkExistingAnalysis();
    }
  }, [isOpen, document]);

  const checkExistingAnalysis = async () => {
    if (!document?.id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      console.log('🔍 Checking existing analysis for:', document.id);
      const response = await fetch(`/api/document-insights/${document.id}`);
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Found existing analysis:', data);
        setAnalysis(data);
        
        // Initialize form data with analysis results
        initializeFormData(data);
        
        // Load intelligent matches and insights
        await loadSmartMatches(data);
        await loadInsights(data);
      } else {
        console.log('ℹ️ No existing analysis found');
        setAnalysis(null);
      }
    } catch (err) {
      console.error('❌ Error checking existing analysis:', err);
      setAnalysis(null);
    } finally {
      setLoading(false);
    }
  };

  const initializeFormData = (analysisData) => {
    const docType = analysisData.document_type || 'general';
    const config = documentTypeConfig[docType] || documentTypeConfig.general;
    
    const initialData = {};
    config.fields.forEach(field => {
      // Map analysis data to form fields
      initialData[field.key] = analysisData[field.key] || analysisData.extracted_data?.[field.key] || '';
    });
    
    setFormData(initialData);
  };

  const loadSmartMatches = async (analysisData) => {
    try {
      const matches = {};
      
      // Check for existing suppliers
      if (analysisData.supplier_name) {
        const { data: suppliers } = await masterSupabase
          .from('suppliers')
          .select('*')
          .ilike('name', `%${analysisData.supplier_name}%`)
          .limit(5);
        matches.suppliers = suppliers || [];
      }
      
      // Check for existing tenants
      if (analysisData.tenant_name || analysisData.contract_party) {
        const searchName = analysisData.tenant_name || analysisData.contract_party;
        const { data: tenants } = await masterSupabase
          .from('tenants')
          .select('*')
          .ilike('name', `%${searchName}%`)
          .limit(5);
        matches.tenants = tenants || [];
      }
      
      // Check for existing properties
      if (analysisData.property_reference) {
        const { data: properties } = await masterSupabase
          .from('properties')
          .select('*')
          .or(`address.ilike.%${analysisData.property_reference}%,name.ilike.%${analysisData.property_reference}%`)
          .limit(5);
        matches.properties = properties || [];
      }
      
      setSmartMatches(matches);
    } catch (error) {
      console.error('Error loading smart matches:', error);
    }
  };

  const loadInsights = async (analysisData) => {
    try {
      // Memory-based insights
      const memoryResponse = await fetch('/api/memory-insights', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          documentType: analysisData.document_type,
          entities: {
            supplier: analysisData.supplier_name,
            tenant: analysisData.tenant_name,
            property: analysisData.property_reference,
            amount: analysisData.amount_gross
          }
        })
      });
      
      if (memoryResponse.ok) {
        const memoryData = await memoryResponse.json();
        setMemoryInsights(memoryData.insights || []);
      }
      
      // External insights (simulated for now - will be real APIs later)
      const externalInsightsData = await generateExternalInsights(analysisData);
      setExternalInsights(externalInsightsData);
      
    } catch (error) {
      console.error('Error loading insights:', error);
    }
  };

  const generateExternalInsights = async (analysisData) => {
    // Simulated external insights - will be replaced with real API calls
    const insights = [];
    
    if (analysisData.document_type === 'invoice' && analysisData.amount_gross) {
      insights.push({
        type: 'benchmark',
        icon: BarChart3,
        title: 'Kostenvergleich',
        content: `Betrag liegt ${Math.random() > 0.5 ? 'über' : 'unter'} dem Durchschnitt für diese Kategorie`,
        color: 'blue'
      });
    }
    
    if (analysisData.document_type === 'contract') {
      insights.push({
        type: 'legal',
        icon: Shield,
        title: 'Rechtliche Hinweise',
        content: 'Kündigungsfristen entsprechen den gesetzlichen Vorgaben',
        color: 'green'
      });
      
      insights.push({
        type: 'market',
        icon: Globe,
        title: 'Marktdaten',
        content: 'Miete liegt im üblichen Bereich für diese Lage',
        color: 'purple'
      });
    }
    
    insights.push({
      type: 'knowledge',
      icon: BookOpen,
      title: 'Wissensdatenbank',
      content: 'Ähnliche Dokumente wurden in der Vergangenheit erfolgreich verarbeitet',
      color: 'orange'
    });
    
    return insights;
  };

  const handleSave = async () => {
    if (!analysis || !formData) return;
    
    setSaving(true);
    try {
      const docType = analysis.document_type || 'general';
      const config = documentTypeConfig[docType] || documentTypeConfig.general;
      
      // Prepare data for saving
      const saveData = {
        ...formData,
        document_id: document.id,
        created_at: new Date().toISOString(),
        analysis_data: analysis
      };
      
      // Save to appropriate table
      const { data, error } = await masterSupabase
        .from(config.saveTable)
        .insert([saveData]);
      
      if (error) throw error;
      
      console.log('✅ Document processed and saved:', data);
      onClose();
      
    } catch (error) {
      console.error('❌ Error saving document:', error);
    } finally {
      setSaving(false);
    }
  };

  const renderSmartField = (field) => {
    const value = formData[field.key] || '';
    const matches = smartMatches[field.table] || [];
    
    return (
      <div key={field.key} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {field.label}
        </label>
        
        {field.type === 'select' && matches.length > 0 ? (
          <select
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          >
            <option value="">Auswählen...</option>
            {matches.map(match => (
              <option key={match.id} value={match.name || match.id}>
                {match.name} {match.address && `(${match.address})`}
              </option>
            ))}
            <option value="__new__">+ Neu anlegen</option>
          </select>
        ) : field.type === 'currency' ? (
          <div className="relative">
            <Euro className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="number"
              step="0.01"
              value={value}
              onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: parseFloat(e.target.value) || 0 }))}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
            />
          </div>
        ) : field.type === 'date' ? (
          <input
            type="date"
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        ) : field.type === 'textarea' ? (
          <textarea
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        ) : (
          <input
            type={field.type === 'number' ? 'number' : 'text'}
            value={value}
            onChange={(e) => setFormData(prev => ({ ...prev, [field.key]: e.target.value }))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
          />
        )}
        
        {matches.length > 0 && field.type === 'select' && (
          <p className="text-xs text-green-600 dark:text-green-400">
            ✅ {matches.length} passende Einträge gefunden
          </p>
        )}
      </div>
    );
  };

  const renderInsightCard = (insight) => {
    const IconComponent = insight.icon;
    const colorClasses = {
      blue: 'border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-900/20',
      green: 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-900/20',
      purple: 'border-purple-200 bg-purple-50 dark:border-purple-800 dark:bg-purple-900/20',
      orange: 'border-orange-200 bg-orange-50 dark:border-orange-800 dark:bg-orange-900/20'
    };
    
    return (
      <div key={insight.title} className={`p-3 rounded-lg border ${colorClasses[insight.color]}`}>
        <div className="flex items-start space-x-3">
          <IconComponent className={`h-5 w-5 mt-0.5 text-${insight.color}-600 dark:text-${insight.color}-400`} />
          <div className="flex-1">
            <h4 className="font-medium text-gray-900 dark:text-white text-sm">
              {insight.title}
            </h4>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
              {insight.content}
            </p>
          </div>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const docType = analysis?.document_type || 'general';
  const config = documentTypeConfig[docType] || documentTypeConfig.general;
  const IconComponent = config.icon;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <IconComponent className={`h-6 w-6 text-${config.color}-600`} />
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {config.title}
              </h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {document?.name || 'Unbekanntes Dokument'}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* 3-Column Content */}
        <div className="flex h-[calc(95vh-120px)]">
          {/* Left Column - Debug Panel */}
          <div className="w-1/4 border-r border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-medium text-gray-900 dark:text-white">Debug & Memory</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDebugOpen(!debugOpen)}
                >
                  <Bug className="h-4 w-4" />
                </Button>
              </div>
              
              {debugOpen && (
                <div className="bg-gray-900 rounded-lg p-3 text-xs font-mono">
                  <div className="text-green-400">
                    Document ID: {document?.id}
                  </div>
                  <div className="text-blue-400">
                    Type: {analysis?.document_type || 'unknown'}
                  </div>
                  <div className="text-yellow-400">
                    Confidence: {((analysis?.confidence || 0) * 100).toFixed(1)}%
                  </div>
                  <div className="text-purple-400">
                    Model: {analysis?.model_used || 'unknown'}
                  </div>
                </div>
              )}
              
              {/* Memory Insights */}
              {memoryInsights.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Memory-Verbindungen
                  </h4>
                  <div className="space-y-2">
                    {memoryInsights.map((insight, idx) => (
                      <div key={idx} className="text-xs p-2 bg-gray-100 dark:bg-gray-700 rounded">
                        {insight.content}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Middle Column - Smart Form */}
          <div className="flex-1 p-6 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : analysis ? (
              <div className="space-y-6">
                <div className="flex items-center space-x-3 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                  <CheckCircle className="h-6 w-6 text-green-500" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Analyse abgeschlossen
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      Dokument wurde erfolgreich analysiert und Felder vorausgefüllt
                    </p>
                  </div>
                </div>

                {/* Smart Form Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {config.fields.map(renderSmartField)}
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <Button variant="outline" onClick={onClose}>
                    Abbrechen
                  </Button>
                  <Button 
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Speichern...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Anlegen
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 dark:text-gray-400">
                    Keine Analyse verfügbar
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Intelligent Insights */}
          <div className="w-1/3 border-l border-gray-200 dark:border-gray-700 p-4 overflow-y-auto">
            <div className="space-y-4">
              <div className="flex items-center space-x-2">
                <Sparkles className="h-5 w-5 text-purple-600" />
                <h3 className="font-medium text-gray-900 dark:text-white">
                  Intelligente Insights
                </h3>
              </div>

              {/* External Insights */}
              {externalInsights.length > 0 && (
                <div className="space-y-3">
                  {externalInsights.map(renderInsightCard)}
                </div>
              )}

              {/* Smart Matches Summary */}
              {Object.keys(smartMatches).length > 0 && (
                <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h4 className="font-medium text-blue-900 dark:text-blue-100 text-sm mb-2">
                    Smart-Matching Ergebnisse
                  </h4>
                  {Object.entries(smartMatches).map(([key, matches]) => (
                    <div key={key} className="text-xs text-blue-700 dark:text-blue-300">
                      {key}: {matches.length} Treffer gefunden
                    </div>
                  ))}
                </div>
              )}

              {/* Future API Integrations Placeholder */}
              <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                <h4 className="font-medium text-gray-700 dark:text-gray-300 text-sm mb-2">
                  Erweiterte Recherche
                </h4>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Weitere APIs werden integriert: Marktdaten, Rechtsdatenbank, Bonitätsprüfung...
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentAnalysisModal;
